import { Badge } from '@/lib/base/badges/badges';
import { Table } from '@/lib/base/table/table';
import type { PortfolioMember, PortfolioTeam } from '@/lib/portfolio/types';

import { MemberRoleControl } from './MemberRoleControl';
import { MemberTeamsControl } from './MemberTeamsControl';

interface MembersTableProps {
  members: PortfolioMember[];
  teams: readonly PortfolioTeam[];
  portfolioId: number;
  orgId: number;
}

// `Status` previously headed a column that rendered a role control with a lifecycle badge beside
// it — two concepts under one heading. The column now heads the role alone, and the deactivated
// state reads as a badge on the member's name, matching how a dead service account renders.
const COLUMNS = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email' },
  { id: 'role', label: 'Role' },
  { id: 'teams', label: 'Teams' },
] as const;

export function MembersTable({
  members,
  teams,
  portfolioId,
  orgId,
}: MembersTableProps) {
  return (
    <Table aria-label='Portfolio members'>
      <Table.Header>
        {COLUMNS.map(col => (
          <Table.Head
            key={col.id}
            id={col.id}
            label={col.label}
            isRowHeader={col.id === 'name'}
          />
        ))}
      </Table.Header>
      <Table.Body>
        {members.map(member => (
          <Table.Row key={member.user_id}>
            <Table.Cell>
              <div className='flex items-center gap-1.5'>
                <span className='font-medium text-[var(--color-text-primary)]'>
                  {member.display_name || member.email}
                </span>
                {member.deactivated && (
                  <Badge color='error' size='sm'>
                    Deactivated
                  </Badge>
                )}
              </div>
            </Table.Cell>
            <Table.Cell>{member.email}</Table.Cell>
            <Table.Cell>
              <MemberRoleControl
                portfolioId={portfolioId}
                orgId={orgId}
                userId={member.user_id}
                displayName={member.display_name || member.email}
                isAdmin={member.is_admin}
              />
            </Table.Cell>
            <Table.Cell>
              <MemberTeamsControl
                portfolioId={portfolioId}
                orgId={orgId}
                userId={member.user_id}
                memberTeamIds={member.teams}
                allTeams={teams}
              />
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}
